import { useState } from 'react';
import { db } from '@/lib/db';
import type { Member } from '@/lib/db/types';
import { validateDisplayName } from '@/lib/display-name';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  member: Member;
  onClose: () => void;
  onSaved: (m: Member) => void;
}

export function EditDisplayNameModal({ member, onClose, onSaved }: Props) {
  const [name, setName] = useState(member.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validationError = name.trim() ? validateDisplayName(name, member.handle) : null;

  const save = async () => {
    const err = validateDisplayName(name, member.handle);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      onSaved(await db.members.update({ name: name.trim() }));
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title="Display Name" onClose={onClose}>
      <div className="p-4">
        <div className="mb-3 text-xs leading-[1.5] text-ink-soft">
          This is how your name appears on the scoreboard. Your username stays @{member.handle}.
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Joe Kimani"
          autoComplete="name"
          className="mb-3.5 w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-[15px] text-ink outline-none"
        />
        {(validationError || error) && (
          <p className="mb-3 text-sm text-bad">{validationError || error}</p>
        )}
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[10px] border border-line bg-paper py-3 text-[13px] font-bold text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || !!validationError}
            className="flex-1 rounded-[10px] bg-navy py-3 text-[13px] font-bold text-paper disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
